import type {ReportPhotoInput} from "@mytour/shared";
import {reportPhotoInputSchema} from "@mytour/shared";
import {Image, StyleSheet, Text, View} from "@react-pdf/renderer";
import type {ReactNode} from "react";

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    gap: "10mm",
    paddingHorizontal: "5mm",
    marginTop: "6mm",
  },
  lastRow: {marginBottom: 8},
  figure: {width: "85mm"},
  image: {width: "85mm", height: "65mm", objectFit: "contain"},
  caption: {fontSize: 8, lineHeight: 1.2, marginTop: 4},
});

export function validateReportPhotos(photos: unknown[], errorMessage: string): void {
  for (const photo of photos) {
    if (!reportPhotoInputSchema.safeParse(photo).success) {
      throw new Error(errorMessage);
    }
  }
}

type RenderedNode = {
  type?: string;
  props?: {id?: string};
  image?: {width?: number; height?: number};
  children?: RenderedNode[];
};

export function assertReportPhotosRendered(
  result: unknown,
  expectedCount: number,
  photoIdPrefix: string,
  errorMessage: string
): void {
  const layout = (result as {_INTERNAL__LAYOUT__DATA_?: RenderedNode})?._INTERNAL__LAYOUT__DATA_;
  let renderedCount = 0;
  function visit(node: RenderedNode): void {
    if (node.type === "IMAGE" && node.props?.id?.startsWith(photoIdPrefix)) {
      if (!node.image?.width || !node.image.height) {
        throw new Error(errorMessage);
      }
      renderedCount += 1;
    }
    for (const child of node.children ?? []) {
      visit(child);
    }
  }
  if (layout) {
    visit(layout);
  }
  if (renderedCount !== expectedCount) {
    throw new Error(errorMessage);
  }
}

function breakLongCaptionWords(word: string): string[] {
  return word.length > 24 ? Array.from(word) : [word];
}

export function ReportPhotos({
  photos,
  photoIdPrefix,
  heading,
}: {
  photos: Partial<ReportPhotoInput>[];
  photoIdPrefix: string;
  heading?: ReactNode;
}) {
  const rowCount = Math.ceil(photos.length / 2);
  return Array.from({length: rowCount}, (_, rowIndex) => {
    const row = (
      <View
        key={rowIndex}
        wrap={false}
        style={rowIndex === rowCount - 1 ? [styles.row, styles.lastRow] : styles.row}
      >
        {photos.slice(rowIndex * 2, rowIndex * 2 + 2).map((photo, columnIndex) => {
          const photoId = `${photoIdPrefix}${rowIndex * 2 + columnIndex}`;
          return (
            <View key={photoId} id={`${photoId}-figure`} wrap={false} style={styles.figure}>
              <Image id={photoId} src={photo.dataUrl} style={styles.image} />
              {photo.caption ? (
                <Text style={styles.caption} hyphenationCallback={breakLongCaptionWords}>
                  {photo.caption}
                </Text>
              ) : null}
            </View>
          );
        })}
      </View>
    );
    return rowIndex === 0 && heading ? (
      <View key={rowIndex} wrap={false}>
        {heading}
        {row}
      </View>
    ) : (
      row
    );
  });
}
